import { ref } from 'vue'
import { useToast } from 'vue-toastification'
import type { BlacklistedEmailDomain, WaypointType } from '@apiTypes'

const toast = useToast()

export function flattenObject(obj: any): Record<string, any> {
  const result: Record<string, any> = {}

  function flatten(nested: any, prevKey = '') {
    for (const [key, value] of Object.entries(nested)) {
      const newKey = prevKey ? `${prevKey}.${key}` : key
      if (isArrayOfObjects(value))
        (<any[]>value).forEach((val: any, index: number) => flatten(val, `${newKey}[${index}]`))
      else if (isObject(value) && !isArray(value))
        flatten(value, newKey)
      else
        result[newKey] = value
    }
  }

  flatten(obj)

  return result
}

function isObject(obj: any): boolean {
  return typeof obj === 'object' && obj != null
}

function isArray(obj: any): boolean {
  return isObject(obj) && Array.isArray(obj)
}

function isArrayOfObjects(obj: any): boolean {
  return obj != null && Array.isArray(obj) && obj.every(val => isObject(val))
}

export function toPascalCase(str: string): string {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('')
}

export type AddressComponents = google.maps.GeocoderAddressComponent[] | undefined

export interface Address {
  point: {
    type?: WaypointType
    coordinates?: number[]
  } | null
  home: string
  postal_code: string
  street: string
  region: string
  city: string
  country: string
  name: string
}

export function getAddressObject(address_components: AddressComponents): Address {
  const ShouldBeComponent: Record<string, string[]> = {
    home: ['street_number'],
    postal_code: ['postal_code'],
    street: ['street_address', 'route'],
    region: [
      'administrative_area_level_1',
      'administrative_area_level_2',
      'administrative_area_level_3',
      'administrative_area_level_4',
      'administrative_area_level_5',
    ],
    city: [
      'locality',
      'postal_town',
      'sublocality',
      'sublocality_level_1',
      'sublocality_level_2',
      'sublocality_level_3',
      'sublocality_level_4',
    ],
    country: ['country'],
  }

  const address: Address = {
    point: null,
    home: '',
    postal_code: '',
    street: '',
    region: '',
    city: '',
    country: '',
    name: '',
  }

  for (const shouldBe in ShouldBeComponent) {
    // Each list is in priority order. Google lists the most specific component first, so going by its
    // order made the city the district (Staré Mesto, Innere Stadt) where it also sends the locality.
    const component: any = ShouldBeComponent[shouldBe]
      .map(type => address_components?.find(item => item.types.includes(type)))
      .find(Boolean)

    if (component) {
      // Handle both old (long_name/short_name) and new (longText/shortText) Google API formats
      const longValue = component.longText || component.long_name
      const shortValue = component.shortText || component.short_name

      address[shouldBe as keyof Address] = shouldBe === 'country' ? shortValue : longValue
    }
  }

  return address
}

export function generateWKTPoint(lat: number, lng: number): string {
  return `SRID=4326;POINT (${lng} ${lat})`
}

export function useWindowWidth(): Ref<number> {
  const windowWidth_ = ref(window.innerWidth)

  const handleResize = () => {
    windowWidth_.value = window.innerWidth
  }

  onMounted(() => window.addEventListener('resize', handleResize))
  onUnmounted(() => window.removeEventListener('resize', handleResize))

  return windowWidth_
}

export function isNullOrUndefined(object: any): object is null | undefined {
  return object === null || object === undefined
}

export const WORKING_HOURS_START = 8
export const WORKING_HOURS_END = 17

export function adjustToWorkingHours(datetime: string | undefined | null, hoursOffset: number): Date {
  const date = datetime ? new Date(datetime) : new Date()
  const initialMinutes = date.getMinutes()

  let remainingHours = hoursOffset
  let loopCount = 0
  const MAX_LOOPS = 100

  while (remainingHours > 0 && loopCount < MAX_LOOPS) {
    loopCount++

    if (isWeekend(date)) {
      skipToNextMonday(date, initialMinutes)
      continue
    }

    ensureWorkingHours(date, initialMinutes)

    const hoursTillEnd = getHoursTillEndOfWorkingDay(date)

    if (hoursTillEnd >= remainingHours) {
      date.setHours(date.getHours() + remainingHours, initialMinutes, 0, 0)
      break
    }

    remainingHours -= hoursTillEnd
    moveToNextWorkingDay(date, initialMinutes)
  }

  return date
}

function isWeekend(date: Date): boolean {
  const dayOfWeek = date.getDay()

  return dayOfWeek === 0 || dayOfWeek === 6
}

function skipToNextMonday(date: Date, initialMinutes: number): void {
  const dayOfWeek = date.getDay()
  const daysToAdd = dayOfWeek === 0 ? 1 : 2

  date.setDate(date.getDate() + daysToAdd)
  date.setHours(WORKING_HOURS_START, initialMinutes, 0, 0)
}

function ensureWorkingHours(date: Date, initialMinutes: number): void {
  if (date.getHours() < WORKING_HOURS_START)
    date.setHours(WORKING_HOURS_START, initialMinutes, 0, 0)
}

function getHoursTillEndOfWorkingDay(date: Date): number {
  return Math.max(0, WORKING_HOURS_END - date.getHours())
}

function moveToNextWorkingDay(date: Date, initialMinutes: number): void {
  date.setDate(date.getDate() + 1)
  date.setHours(WORKING_HOURS_START, initialMinutes, 0, 0)
}

export function clampToWorkingHours(date: Date): Date {
  const adjusted = new Date(date)

  // Move to a weekday if on a weekend
  while (adjusted.getDay() === 0 || adjusted.getDay() === 6) {
    adjusted.setDate(adjusted.getDate() + 1)
    adjusted.setHours(WORKING_HOURS_START, 0, 0, 0)
  }

  const hour = adjusted.getHours()

  // If before start time
  if (hour < WORKING_HOURS_START) {
    adjusted.setHours(WORKING_HOURS_START, 0, 0, 0)
  }

  // If after end time, move to next valid day
  else if (hour >= WORKING_HOURS_END) {
    do
      adjusted.setDate(adjusted.getDate() + 1)
    while (adjusted.getDay() === 0 || adjusted.getDay() === 6)

    adjusted.setHours(WORKING_HOURS_START, 0, 0, 0)
  }

  return adjusted
}

export function clampToWorkingHoursBackward(date: Date): Date {
  const adjusted = new Date(date)
  const hour = adjusted.getHours()
  const dayOfWeek = adjusted.getDay()

  // If it's a weekend, move to Friday 17:00
  if (dayOfWeek === 0) { // Sunday
    adjusted.setDate(adjusted.getDate() - 2) // Go to Friday
    adjusted.setHours(WORKING_HOURS_END, 0, 0, 0)
  }
  else if (dayOfWeek === 6) { // Saturday
    adjusted.setDate(adjusted.getDate() - 1) // Go to Friday
    adjusted.setHours(WORKING_HOURS_END, 0, 0, 0)
  }

  // If it's after working hours, move to end of same day
  else if (hour >= WORKING_HOURS_END) {
    adjusted.setHours(WORKING_HOURS_END, 0, 0, 0)
  }

  // If it's before working hours
  else if (hour < WORKING_HOURS_START) {
    // Move to previous working day at 17:00
    do
      adjusted.setDate(adjusted.getDate() - 1)
    while (adjusted.getDay() === 0 || adjusted.getDay() === 6)

    adjusted.setHours(WORKING_HOURS_END, 0, 0, 0)
  }

  // If it's during working hours, keep it as is

  return adjusted
}

export function splitName(name: string): [string, string] {
  const firstSpaceIndex = (name || '').indexOf(' ')
  if (firstSpaceIndex >= 0) {
    const firstPart = name.substring(0, firstSpaceIndex)
    const secondPart = name.substring(firstSpaceIndex + 1)

    return [firstPart, secondPart]
  }

  return [name || '', '']
}

export function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function checkEmailDomainBlacklisted(email: string, blacklistedDomains: Ref<BlacklistedEmailDomain[]>): boolean {
  const normalizedEmail = (email.toLowerCase()).trim()

  for (const item of blacklistedDomains.value) {
    if (normalizedEmail.endsWith(item.domain))
      return true
  }

  return false
}

export function getUserTimeZone(): string {
  let userTimeZone = 'UTC' // Default fallback

  if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    try {
      const detectedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (detectedTimeZone)
        userTimeZone = detectedTimeZone
      else
        console.warn('Timezone detection returned undefined. Falling back to UTC.')
    }
    catch (error) {
      console.warn('Error detecting timezone. Falling back to UTC.', error)
    }
  }
  else {
    console.warn('Intl.DateTimeFormat is not supported. Falling back to UTC.')
  }

  return userTimeZone
}

type ToastType = 'error' | 'warning' | 'success' | 'info'

export function showToast(message: string, type: ToastType = 'error') {
  toast.clear()
  switch (type) {
    case 'warning':
      toast.warning(message)
      break
    case 'success':
      toast.success(message)
      break
    case 'info':
      toast.info(message)
      break
    case 'error':
    default:
      toast.error(message)
      break
  }
}
